import {
    Box,
    CircularProgress,
    Dialog,
    handleClose,
    newToast,
    useParams,
} from "@compass/core";
import { Student, StudentCustomIdType } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { StudentQrPageHeader } from "./StudentQrPageHeader";
import { StudentQrPageBody } from "./StudentQrPageBody";
import ApiService from "services/apiService";

const routeName = "studentQr";

class Params {
    studentId: number = 0;
}

interface StudentQrPageLocationState {
    customIdTypes?: StudentCustomIdType[];
}

export const StudentQrPage = () => {
    const { params } = useParams(routeName, Params);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const locationState = state as StudentQrPageLocationState | null;
    const [student, setStudent] = useState<Student>({} as Student);
    const [customIdTypes, setCustomIdTypes] = useState<StudentCustomIdType[]>();
    const [isStudentLoading, setIsStudentLoading] = useState<boolean>(true);
    const [areCustomIdTypesLoading, setAreCustomIdTypesLoading] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        let ignore = false;
        const studentId = params.studentId;

        setStudent({} as Student);
        setIsStudentLoading(true);
        ApiService.students.get(studentId)
            .then((response) => {
                if (!ignore) setStudent(response);
            })
            .catch((error) => {
                if (!ignore) {
                    console.error('Failed to load student:', error);
                    dispatch(setAppToast(newToast("error", "Failed to load student.")));
                }
            })
            .finally(() => {
                if (!ignore) setIsStudentLoading(false);
            });

        if (locationState?.customIdTypes) {
            setCustomIdTypes(locationState.customIdTypes);
            setAreCustomIdTypesLoading(false);
        } else {
            setAreCustomIdTypesLoading(true);
            ApiService.students.getViewResource(studentId)
                .then((response) => {
                    if (!ignore) setCustomIdTypes(response.customIdTypes || []);
                })
                .catch((error) => {
                    if (!ignore) {
                        console.error('Failed to load student view resource:', error);
                        setCustomIdTypes([]);
                    }
                })
                .finally(() => {
                    if (!ignore) setAreCustomIdTypesLoading(false);
                });
        }

        return () => {
            ignore = true;
        };
    }, [params.studentId, dispatch]);

    const updateStudent = (updater: (draft: Student) => void) => {
        setStudent((currentStudent) => produce(currentStudent, updater));
    };

    return (
        <Dialog
            open={true}
            onClose={() => handleClose(navigate)}
            width="min(720px, 95vw)"
            maxWidth={false}
            header={<StudentQrPageHeader />}
            content={
                isStudentLoading || areCustomIdTypesLoading ? (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <CircularProgress />
                    </Box>
                ) : student.id === params.studentId ? (
                    <StudentQrPageBody
                        student={student}
                        customIdTypes={customIdTypes || []}
                        setStudent={updateStudent}
                        isLoading={isLoading}
                        setIsLoading={setIsLoading}
                    />
                ) : null
            }
        />
    );
};
